import * as Sentry from '@sentry/react-router'
import { OpenImgContextProvider } from 'openimg/react'
import { useEffect } from 'react'
import {
  data,
  Link,
  Links,
  Meta,
  NavLink,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
} from 'react-router'
import { HoneypotProvider } from 'remix-utils/honeypot/react'
import { type Route } from './+types/root.ts'
import appleTouchIconAssetUrl from './assets/favicons/apple-touch-icon.png'
import faviconAssetUrl from './assets/favicons/favicon.svg'
import { GeneralErrorBoundary } from './components/error-boundary.tsx'
import { InstallHint } from './components/install-hint.tsx'
import { Logo } from './components/logo.tsx'
import { EpicProgress } from './components/progress-bar.tsx'
import { useToast } from './components/toaster.tsx'
import { Button } from './components/ui/button.tsx'
import { href as iconsHref } from './components/ui/icon.tsx'
import { EpicToaster } from './components/ui/sonner.tsx'
import { UserDropdown } from './components/user-dropdown.tsx'
import {
  ThemeSwitch,
  useOptionalTheme,
  useTheme,
} from './routes/resources+/theme-switch.tsx'
import tailwindStyleSheetUrl from './styles/tailwind.css?url'
import { getUserId, logout } from './utils/auth.server.ts'
import { ClientHintCheck, getHints } from './utils/client-hints.tsx'
import { prisma } from './utils/db.server.ts'
import { getEnv } from './utils/env.server.ts'
import { pipeHeaders } from './utils/headers.server.ts'
import { honeypot } from './utils/honeypot.server.ts'
import { combineHeaders, getDomainUrl, getImgSrc } from './utils/misc.tsx'
import { useNonce } from './utils/nonce-provider.ts'
import { type Theme, getTheme } from './utils/theme.server.ts'
import { makeTimings, time } from './utils/timing.server.ts'
import { registerServiceWorker } from './utils/service-worker.client.ts'
import { getToast } from './utils/toast.server.ts'
import { useOptionalUser } from './utils/user.ts'

export const links: Route.LinksFunction = () => {
  return [
    // Preload svg sprite as a resource to avoid render blocking
    { rel: 'preload', href: iconsHref, as: 'image' },
    {
      rel: 'icon',
      href: '/favicon.ico',
      sizes: '48x48',
    },
    { rel: 'icon', type: 'image/svg+xml', href: faviconAssetUrl },
    { rel: 'apple-touch-icon', href: appleTouchIconAssetUrl },
    {
      rel: 'manifest',
      href: '/site.webmanifest',
      crossOrigin: 'use-credentials',
    } as const, // necessary to make typescript happy
    { rel: 'stylesheet', href: tailwindStyleSheetUrl },
  ].filter(Boolean)
}

export const meta: Route.MetaFunction = ({ loaderData }) => {
  return [
    { title: loaderData ? 'Youth Rollcall' : 'Error | Youth Rollcall' },
    { name: 'description', content: `Track attendance for youth programs` },
  ]
}

export async function loader({ request }: Route.LoaderArgs) {
  const timings = makeTimings('root loader')
  const userId = await time(() => getUserId(request), {
    timings,
    type: 'getUserId',
    desc: 'getUserId in root',
  })

  // Opaque id only (no name/email), so errors can be traced to a user
  // without sending personal data to Sentry.
  if (userId) Sentry.setUser({ id: userId })

  const user = userId
    ? await time(
      () =>
        prisma.user.findUnique({
          select: {
            id: true,
            name: true,
            username: true,
            image: { select: { objectKey: true } },
            roles: {
              select: {
                name: true,
                permissions: {
                  select: { entity: true, action: true, access: true },
                },
              },
            },
          },
          where: { id: userId },
        }),
      { timings, type: 'find user', desc: 'find user in root' },
    )
    : null

  // Admins see the full program switcher; check if a non-admin user has
  // access to exactly one program (as PROGRAM_LEAD, TEACHER, or TEACHING_ASSISTANT)
  const isAdmin = user?.roles.some(role => role.name === 'admin') ?? false
  const primaryProgram = userId && !isAdmin
    ? await time(
      async () => {
        const userPrograms = await prisma.programMember.findMany({
          where: {
            userId,
            role: { in: ['PROGRAM_LEAD', 'TEACHER', 'TEACHING_ASSISTANT'] },
          },
          include: {
            program: {
              select: {
                id: true,
                name: true,
              },
            },
          },
        })
        return userPrograms.length === 1 ? userPrograms[0]?.program : null
      },
      { timings, type: 'find primary program', desc: 'find primary program in root' },
    )
    : null
  if (userId && !user) {
    console.info('something weird happened')
    // something weird happened... The user is authenticated but we can't find
    // them in the database. Maybe they were deleted? Let's log them out.
    await logout({ request, redirectTo: '/' })
  }
  const { toast, headers: toastHeaders } = await getToast(request)
  const honeyProps = await honeypot.getInputProps()

  return data(
    {
      user,
      primaryProgram,
      requestInfo: {
        hints: getHints(request),
        origin: getDomainUrl(request),
        path: new URL(request.url).pathname,
        userPrefs: {
          theme: getTheme(request),
        },
      },
      ENV: getEnv(),
      toast,
      honeyProps,
    },
    {
      headers: combineHeaders(
        { 'Server-Timing': timings.toString() },
        toastHeaders,
      ),
    },
  )
}

export const headers: Route.HeadersFunction = pipeHeaders

function Document({
  children,
  nonce,
  theme = 'light',
  env = {},
}: {
  children: React.ReactNode
  nonce: string
  theme?: Theme
  env?: Record<string, string | undefined>
}) {
  const allowIndexing = ENV.ALLOW_INDEXING !== 'false'
  return (
    <html lang="en" className={`${theme} h-full overflow-x-hidden`}>
      <head>
        <ClientHintCheck nonce={nonce} />
        <Meta />
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1,viewport-fit=cover" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-title" content="Youth Rollcall" />
        {allowIndexing ? null : (
          <meta name="robots" content="noindex, nofollow" />
        )}
        <Links />
      </head>
      <body className="bg-background text-foreground">
        {children}
        <script
          nonce={nonce}
          dangerouslySetInnerHTML={{
            __html: `window.ENV = ${JSON.stringify(env)}`,
          }}
        />
        <ScrollRestoration nonce={nonce} />
        <Scripts nonce={nonce} />
      </body>
    </html>
  )
}

export function Layout({ children }: { children: React.ReactNode }) {
  // if there was an error running the loader, data could be missing
  const data = useLoaderData<typeof loader | null>()
  const nonce = useNonce()
  const theme = useOptionalTheme()
  return (
    <Document nonce={nonce} theme={theme} env={data?.ENV}>
      {children}
    </Document>
  )
}

function App() {
  const data = useLoaderData<typeof loader>()
  const user = useOptionalUser()
  const theme = useTheme()
  useToast(data.toast)
  // Lets end-to-end tests wait until click handlers are attached; a click on
  // server-rendered markup before hydration falls through to plain links.
  useEffect(() => {
    document.documentElement.dataset.hydrated = 'true'
  }, [])
  useEffect(() => {
    registerServiceWorker()
  }, [])
  const userId = user?.id
  useEffect(() => {
    Sentry.setUser(userId ? { id: userId } : null)
  }, [userId])

  return (
    <OpenImgContextProvider
      optimizerEndpoint="/resources/images"
      getSrc={getImgSrc}
    >
      <div className="flex min-h-screen flex-col justify-between">
        <header className="container py-6">
          <nav className="flex flex-wrap items-center justify-between gap-4 sm:flex-nowrap md:gap-8">
            <HomeLink />
            {user && (
              <div className="hidden md:flex items-center gap-6">
                <Link to="/dashboard" className="text-sm font-medium hover:text-brand transition-colors">
                  Dashboard
                </Link>
                <Link to={data.primaryProgram ? `/programs/${data.primaryProgram.id}` : "/programs"} className="text-sm font-medium hover:text-brand transition-colors">
                  {data.primaryProgram ? `${data.primaryProgram.name} Program` : "Programs"}
                </Link>
                <Link to="/attendance" className="text-sm font-medium hover:text-brand transition-colors">
                  Attendance
                </Link>
                <Link to="/analytics" className="text-sm font-medium hover:text-brand transition-colors">
                  Analytics
                </Link>
              </div>
            )}
            <div className="flex items-center gap-10">
              {user ? (
                <UserDropdown />
              ) : (
                <Button asChild variant="default" size="lg">
                  <Link to="/login">Log In</Link>
                </Button>
              )}
            </div>
          </nav>
        </header>

        <div className="flex flex-1 flex-col">
          {user && <InstallHint />}
          <Outlet />
        </div>

        {user && (
          <nav
            aria-label="Main"
            className="bg-background/95 fixed inset-x-0 bottom-0 z-40 grid grid-cols-4 border-t backdrop-blur-sm md:hidden"
            style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
          >
            {[
              { to: '/dashboard', label: 'Today' },
              { to: data.primaryProgram ? `/programs/${data.primaryProgram.id}` : '/programs', label: 'Program' },
              { to: '/attendance', label: 'Attendance' },
              { to: '/analytics', label: 'Analytics' },
            ].map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `flex min-h-14 items-center justify-center border-t-2 text-xs font-medium transition-colors ${isActive ? 'border-primary text-brand' : 'border-transparent text-muted-foreground'}`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        )}

        {/* On phones the fixed bottom nav (3.5rem tall, plus the safe area) overlays
            the end of the page, so the footer reserves room for it */}
        <div
          className={`container flex justify-between items-center ${user ? 'pb-[calc(5rem+env(safe-area-inset-bottom))] md:pb-5' : 'pb-5'}`}
        >
          <HomeLink />
          <div className="flex items-center gap-6">
            {/* <a
              href="https://yahyagilany.io"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-muted-foreground hover:text-brand transition-colors"
            >
              Developed by Yahya Gilany
            </a> */}
            <ThemeSwitch userPreference={data.requestInfo.userPrefs.theme} />
          </div>
        </div>
      </div>
      <EpicToaster closeButton position="top-center" theme={theme} />
      <EpicProgress />
    </OpenImgContextProvider>
  )
}

function HomeLink({ className }: { className?: string }) {
  return (
    <Link to="/" aria-label="Youth Rollcall home" className="rounded-md">
      <Logo className={className} />
    </Link>
  )
}

function AppWithProviders() {
  const data = useLoaderData<typeof loader>()
  return (
    <HoneypotProvider {...data.honeyProps}>
      <App />
    </HoneypotProvider>
  )
}

export default AppWithProviders

// this is a last resort error boundary. There's not much useful information we
// can offer at this level.
export const ErrorBoundary = GeneralErrorBoundary
